import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, History } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useLookups } from "@/lib/useLookups";
import CatalogPage from "@/pages/CatalogPage";
import { Field, inputCls, Empty, StatusBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const COLORS = ["#C5A059", "#8B6F5A", "#4A6B82", "#2D6A4F", "#A0525C", "#6B5B95", "#121212"];

const HistoryList = ({ items, testId }) => (
  items.length === 0 ? <Empty testId={`${testId}-empty`}>Nessun trattamento.</Empty> : (
    <ul className="divide-y divide-[#E8E1D7]" data-testid={testId}>
      {items.map((t) => (
        <li key={t.id} className="flex items-center gap-3 py-3" data-testid={`op-history-row-${t.id}`}>
          <div className="w-24 text-sm text-[#6E675F]">{fmtDate(t.date)}</div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm font-medium">{t.treatment_name}</div>
            <Link to={`/clienti/${t.client_id}`} className="text-xs text-[#C5A059] hover:underline">{t.client_name || "Cliente"}</Link>
          </div>
          <StatusBadge status={t.status} />
        </li>
      ))}
    </ul>
  )
);

const OperatorStats = ({ op }) => {
  const [h, setH] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get(`/operators/${op.id}/history`).then((r) => setH(r.data)); }, [op.id]);
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#9E958A]">
        <span>Effettuati: <b className="text-[#2D6A4F]" data-testid={`operator-done-${op.id}`}>{h?.done.length ?? "…"}</b></span>
        <span>Da effettuare: <b className="text-[#8A6A22]" data-testid={`operator-todo-${op.id}`}>{h?.todo.length ?? "…"}</b></span>
        <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-[#C5A059] hover:underline" data-testid={`operator-history-${op.id}`}><History className="h-3 w-3" /> Storico</button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="operator-history-dialog">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">{op.name} · trattamenti</DialogTitle></DialogHeader>
          {h && (
            <Tabs defaultValue="done">
              <TabsList className="mb-3 bg-[#F6F1EA] p-1 rounded-full">
                <TabsTrigger value="done" className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]" data-testid="op-tab-done">Effettuati ({h.done.length})</TabsTrigger>
                <TabsTrigger value="todo" className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]" data-testid="op-tab-todo">Da effettuare ({h.todo.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="done"><HistoryList items={h.done} testId="op-history-done" /></TabsContent>
              <TabsContent value="todo"><HistoryList items={h.todo} testId="op-history-todo" /></TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function Operators() {
  const { operators, reload } = useLookups(["operators"]);
  return (
    <CatalogPage resource="operators" overline="Staff" title="Operatori" singular="Operatore" testPrefix="operators" items={operators} reload={reload}
      blank={{ name: "", role: "", phone: "", color: COLORS[0], active: true }}
      fields={(f, set) => (
        <>
          <Field label="Nome *"><input className={inputCls} value={f.name} onChange={(e) => set({ ...f, name: e.target.value })} data-testid="operator-name-input" /></Field>
          <Field label="Ruolo"><input className={inputCls} placeholder="Es. Estetista" value={f.role} onChange={(e) => set({ ...f, role: e.target.value })} data-testid="operator-role-input" /></Field>
          <Field label="Telefono"><input className={inputCls} value={f.phone} onChange={(e) => set({ ...f, phone: e.target.value })} data-testid="operator-phone-input" /></Field>
          <Field label="Colore in agenda">
            <div className="flex flex-wrap gap-2 pt-1">
              {COLORS.map((c) => <button key={c} type="button" onClick={() => set({ ...f, color: c })} data-testid={`operator-color-${c.slice(1)}`} className={`h-7 w-7 rounded-full border-2 ${f.color === c ? "border-[#121212] scale-110" : "border-white"}`} style={{ background: c }} />)}
            </div>
          </Field>
        </>
      )}
      renderCard={(o) => (
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-serif text-lg text-white" style={{ background: o.color }}>{o.name.slice(0, 1).toUpperCase()}</div>
          <div className="min-w-0">
            <div className="font-serif text-2xl text-[#121212] truncate">{o.name}</div>
            <div className="text-sm text-[#6E675F]">{o.role || "—"}</div>
            {o.phone && <div className="mt-1 flex items-center gap-1 text-xs text-[#6E675F]"><Phone className="h-3 w-3" />{o.phone}</div>}
            <OperatorStats op={o} />
          </div>
        </div>
      )} />
  );
}
